import React, { memo, useEffect, useMemo } from 'react';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import {
  TextField,
  MenuItem,
  InputAdornment,
  TextFieldProps,
} from '@mui/material';

const FIELD_TYPES = {
  TEXT: 'text',
  DATE: 'date',
  SELECT: 'select',
  NUMERIC: 'numeric',
  TEL: 'tel',
} as const;

type FieldType = (typeof FIELD_TYPES)[keyof typeof FIELD_TYPES];

interface Option {
  label: string;
  value: string | number;
}

interface ExtraProps {
  prefix?: string;
  inputProps?: object;
}

interface FormFieldProps {
  name: string;
  label: string;
  type?: FieldType;
  options?: Option[];
  disabled?: boolean;
  dependentField?: string;
  dependentOptions?: Option[];
  fetchOptions?: (value: string) => void;
  extra?: ExtraProps;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = FIELD_TYPES.TEXT,
  options = [],
  disabled = false,
  dependentField,
  dependentOptions = [],
  fetchOptions,
  extra,
}) => {
  const getOptions = (): Option[] =>
    dependentField ? dependentOptions : options;

  const { values } = useFormikContext<Record<string, unknown>>();

  const dependentValue = useMemo(
    () => (dependentField ? (getIn(values, dependentField) as string) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );
  useEffect(() => {
    if (dependentField && fetchOptions) {
      if (dependentValue) {
        fetchOptions(dependentValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentValue]);

  const renderField = (
    field: FieldProps['field'],
    _form: FieldProps['form'],
    meta: FieldProps['meta'],
    value: string | number | undefined
  ) => {
    const hasError = Boolean(meta.touched && meta.error);

    const commonProps: TextFieldProps = {
      ...field,
      label,
      fullWidth: true,
      disabled,
      error: hasError,
      helperText: hasError ? meta.error : '',
    };

    switch (type) {
      case FIELD_TYPES.DATE:
        return (
          <TextField
            {...commonProps}
            type="date"
            InputProps={{
              inputProps: extra?.inputProps,
            }}
            InputLabelProps={{ shrink: true }}
          />
        );

      case FIELD_TYPES.NUMERIC:
        return (
          <TextField
            {...commonProps}
            InputProps={{
              inputProps: {
                inputMode: 'numeric',
                ...extra?.inputProps,
              },
            }}
            type="text"
          />
        );

      case FIELD_TYPES.SELECT:
        return (
          <TextField
            {...commonProps}
            InputProps={{
              inputProps: extra?.inputProps,
            }}
            select
            value={value || ''}
          >
            {getOptions()?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );

      case FIELD_TYPES.TEL:
        return (
          <TextField
            {...commonProps}
            type="tel"
            InputProps={{
              inputProps: { pattern: '[0-9]*', ...extra?.inputProps },
              startAdornment: extra?.prefix ? (
                <InputAdornment position="start">{extra.prefix}</InputAdornment>
              ) : null,
            }}
          />
        );

      default:
        return (
          <TextField
            {...commonProps}
            InputProps={{
              inputProps: extra?.inputProps,
            }}
          />
        );
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => {
        const value = getIn(form?.values, name) as string | number | undefined; // Access 'form' object here
        return renderField(field, form, meta, value);
      }}
    </Field>
  );
};

export default memo(FormField);
